import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Button, Snackbar, Alert, Chip, Stack, Avatar } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import chatbotService from '../services/ChatbotService';
import HelpButton from './HelpButton';

function isNetBotChatbot(bot) {
    return String(bot?.vendor ?? '').trim() === 'NetBot';
}

function getChatbotInitial(bot) {
    return String(bot?.chatbotName || bot?.name || bot?.chatbotId || 'N')
        .trim()
        .charAt(0)
        .toUpperCase() || 'N';
}

function Chatbots({
    title,
    editBasePath = '/documents/chatbot',
    autoOpenSingle = false,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [chatbots, setChatbots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchChatbots = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await chatbotService.getChatbots();
            setChatbots(Array.isArray(data) ? data.filter(isNetBotChatbot) : []);
        } catch (e) {
            console.error('Error fetching chatbots', e);
            setError(t('chatbots.fetchError', 'Failed to load chatbots'));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        fetchChatbots();
    }, [fetchChatbots]);

    useEffect(() => {
        if (!autoOpenSingle || loading || chatbots.length !== 1) return;

        const id = String(chatbots[0]?.chatbotId || '').trim();
        if (!id) return;
        navigate(`${editBasePath}/${encodeURIComponent(id)}`, { replace: true });
    }, [autoOpenSingle, chatbots, editBasePath, loading, navigate]);

    const handleCloseError = () => setError('');

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="h5" fontWeight="bold">
                        {title || t('sideBar.chatbotMenu', 'Chatbots')}
                    </Typography>
                    <HelpButton />
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={fetchChatbots}
                    disabled={loading}
                    sx={{ textTransform: 'none', borderRadius: 3 }}
                >
                    {loading
                        ? t('chatbots.refreshing', 'Refreshing...')
                        : t('chatbots.refresh', 'Refresh')}
                </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200', width: 96 }}>
                                {t('chatbots.image', 'Image')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('chatbots.name', 'Name')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('chatbots.initSuggestions', 'Initial Suggestions')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {chatbots.map((bot) => (
                            <TableRow key={bot.chatbotId} hover>
                                <TableCell>
                                    <Avatar
                                        src={bot.imageUrl || undefined}
                                        alt={bot.chatbotName || bot.name || bot.chatbotId || 'Chatbot'}
                                        sx={{ width: 44, height: 44, bgcolor: '#bdbdbd', color: '#ffffff' }}
                                    >
                                        {getChatbotInitial(bot)}
                                    </Avatar>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        component={RouterLink}
                                        to={`${editBasePath}/${encodeURIComponent(bot.chatbotId)}`}
                                        variant="text"
                                        sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                                    >
                                        {bot.chatbotName || bot.name || '-'}
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    {Array.isArray(bot.initSuggestions) && bot.initSuggestions.length > 0 ? (
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            {bot.initSuggestions.map((s, idx) => (
                                                <Chip key={idx} label={s} size="small" sx={{ mb: 0.5 }} />
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {t('chatbots.noSuggestions', 'None')}
                                        </Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {chatbots.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    {t('chatbots.empty', 'No chatbots found.')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Chatbots;
